import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Bot, Send, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FALLBACK_REPLY = "Não consegui responder agora. Tente novamente em instantes.";

function getSessionId() {
  let id = localStorage.getItem("fw-chat-session");
  if (!id) {
    id = window.crypto?.randomUUID
      ? window.crypto.randomUUID()
      : `s-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem("fw-chat-session", id);
  }
  return id;
}

function TypingDots() {
  return (
    <span className="flex items-center gap-1 px-1 py-2" aria-label="Assistente digitando">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="h-1.5 w-1.5 animate-bounce rounded-full bg-brand-blue"
          style={{ animationDelay: `${i * 0.15}s` }}
        />
      ))}
    </span>
  );
}

function Bubble({ message }) {
  const isUser = message.role === "user";
  return (
    <div className={isUser ? "flex justify-end" : "flex justify-start"}>
      <div
        className={
          isUser
            ? "max-w-[85%] rounded-2xl rounded-br-md bg-gradient-to-r from-brand-tealight to-brand-blue px-4 py-2.5 text-sm text-slate-950"
            : "max-w-[85%] rounded-2xl rounded-bl-md border border-white/10 bg-white/[0.04] px-4 py-2.5 text-sm leading-relaxed text-slate-100"
        }
      >
        {message.content}
      </div>
    </div>
  );
}

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const listRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      const t = setTimeout(() => inputRef.current?.focus(), 250);
      return () => clearTimeout(t);
    }
  }, [open]);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight });
  }, [messages]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    const history = [...messages, { role: "user", content: text }];
    setMessages([...history, { role: "assistant", content: "" }]);
    setStreaming(true);

    const setLast = (content) =>
      setMessages((prev) => {
        const copy = [...prev];
        copy[copy.length - 1] = { role: "assistant", content };
        return copy;
      });

    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sessionId: getSessionId(),
          message: text,
          history: history.slice(-10),
        }),
      });
      if (!res.ok || !res.body) throw new Error("bad response");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let acc = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const payload = JSON.parse(line.slice(5).trim());
          if (payload.delta) {
            acc += payload.delta;
            setLast(acc);
          } else if (payload.error) {
            setLast(payload.error);
          }
        }
      }
      if (!acc) setLast(FALLBACK_REPLY);
    } catch (e) {
      setLast(FALLBACK_REPLY);
    } finally {
      setStreaming(false);
    }
  };

  return (
    <>
      <button
        type="button"
        data-testid="chat-launcher-btn"
        aria-label="Abrir assistente virtual da FW Team"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-5 left-5 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-r from-brand-tealight to-brand-blue text-slate-950 shadow-[0_8px_30px_rgba(53,130,124,0.4)] transition-all hover:scale-105 active:scale-95"
      >
        {open ? <X className="h-6 w-6" /> : <Bot className="h-6 w-6" />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-panel"
            role="dialog"
            aria-label="Assistente virtual da FW Team"
            initial={{ opacity: 0, y: 16, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.98 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="fixed bottom-[88px] left-4 right-4 z-50 flex h-[min(560px,70vh)] flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#0d1117]/95 shadow-[0_20px_60px_rgba(0,0,0,0.6)] backdrop-blur-xl sm:left-5 sm:right-auto sm:w-[380px]"
          >
            <div className="flex items-center gap-3 border-b border-white/10 px-4 py-3.5">
              <span className="flex h-9 w-9 items-center justify-center rounded-xl border border-brand-teal/40 bg-brand-teal/15 text-brand-blue">
                <Bot className="h-5 w-5" />
              </span>
              <div className="flex-1">
                <p className="font-display text-sm font-bold text-white">Assistente FW Team</p>
                <p className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-emerald-400">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                  Online
                </p>
              </div>
              <button
                type="button"
                data-testid="chat-close-btn"
                aria-label="Fechar assistente"
                onClick={() => setOpen(false)}
                className="flex h-9 w-9 items-center justify-center rounded-xl border border-white/10 text-slate-300 transition-colors hover:border-brand-teal/50 hover:text-brand-blue"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div
              ref={listRef}
              data-lenis-prevent
              data-testid="chat-messages"
              className="flex-1 space-y-3 overflow-y-auto px-4 py-4"
            >
              {messages.length === 0 ? (
                <div className="flex h-full flex-col items-center justify-center gap-3 text-center">
                  <span className="flex h-14 w-14 items-center justify-center rounded-full border border-brand-teal/40 bg-brand-teal/15 text-brand-blue">
                    <Bot className="h-7 w-7" />
                  </span>
                  <p className="max-w-[240px] text-sm leading-relaxed text-slate-300">
                    Olá! Sou o assistente da FW Team. Pergunte sobre os treinos, os serviços
                    ou como começar.
                  </p>
                </div>
              ) : (
                messages.map((m, i) =>
                  !streaming || m.content || i < messages.length - 1 ? (
                    <Bubble key={i} message={m} />
                  ) : (
                    <div key={i} className="flex justify-start">
                      <div className="rounded-2xl rounded-bl-md border border-white/10 bg-white/[0.04] px-2">
                        <TypingDots />
                      </div>
                    </div>
                  ),
                )
              )}
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
              className="border-t border-white/10 p-3"
            >
              <div className="flex items-center gap-2">
                <input
                  ref={inputRef}
                  data-testid="chat-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Digite sua pergunta..."
                  maxLength={800}
                  aria-label="Sua pergunta para o assistente"
                  className="flex-1 rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none"
                />
                <button
                  type="submit"
                  data-testid="chat-send-btn"
                  aria-label="Enviar pergunta"
                  disabled={!input.trim() || streaming}
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-r from-brand-tealight to-brand-blue text-slate-950 transition-all hover:scale-105 active:scale-95 disabled:opacity-40 disabled:hover:scale-100"
                >
                  <Send className="h-4 w-4" />
                </button>
              </div>
              <p className="mt-2 text-center text-[10px] text-slate-500">
                IA pode errar. Para detalhes, fale com Fernando no WhatsApp.
              </p>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
